import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faHeart,
  faHeart as faRegHeart,
  faShoppingCart,
  faPlus,
  faMinus,
  faShareAlt,
  faMapMarkerAlt,
  faStore,
  faUserTie,
  faTag,
  faTruck,
  faCheckCircle,
  faBoxOpen,
  faCircleExclamation,
  faCircleCheck,
  faStar,
  faFlask,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";

import ProductCard from "./components/ProductCard";
import productsData from "./fallback/products";
import { useHerKart } from "./hooks/useHerKart";
import AILightComponent from "../../components/AILightComponent";

const HerKartProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    fetchCart,
    syncCart,
    fetchSaved,
    syncSaved,
    submitFeedback,
    submitSampleRequest,
    fetchLiveProducts,
  } = useHerKart();

  const [liveProducts, setLiveProducts] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(true);

  const [cart, setCart] = useState({});
  const [savedIds, setSavedIds] = useState([]);
  const [loadingKart, setLoadingKart] = useState(true);

  const [message, setMessage] = useState(null);

  // Feedback form state
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Sample request form state
  const [showSampleForm, setShowSampleForm] = useState(false);
  const [sampleNote, setSampleNote] = useState("");
  const [submittingSample, setSubmittingSample] = useState(false);

  const [isSpeakingTTS, setIsSpeakingTTS] = useState(false);
  const [enableAskAI, setEnableAskAI] = useState(false);

  const handleToggleSpeakTTS = () => {
    if (!product?.tts || !("speechSynthesis" in window)) return;

    if (isSpeakingTTS) {
      window.speechSynthesis.cancel();
      setIsSpeakingTTS(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(product.tts);
    utterance.lang = "en-IN";
    utterance.onend = () => setIsSpeakingTTS(false);
    utterance.onerror = () => setIsSpeakingTTS(false);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setIsSpeakingTTS(true);
  };

  // Serialized write queues — guarantee cart/saved updates persist in order.
  const cartQueueRef = useRef(Promise.resolve());
  const savedQueueRef = useRef(Promise.resolve());

  // const safeProducts = useMemo(() => productsData.filter(Boolean), []);
  const safeProducts = useMemo(
    () => productsData.filter(Boolean).map((p) => ({ ...p, isSample: true })),
    [],
  );

  const buildCartItems = (cartMap) => {
    return Object.entries(cartMap).map(([productId, quantity]) => {
      const p =
        allProducts.find((item) => String(item._id) === String(productId)) ||
        safeProducts.find((item) => String(item._id) === String(productId));

      return {
        productId,
        name: p?.name || "",
        store: p?.store || "",
        entrepreneur: p?.entrepreneur || "",
        city: p?.city || "",
        price: Number(p?.sellingPrice || p?.price || 0),
        quantity,
        image: p?.images?.[0] || p?.image || "",
      };
    });
  };

  const buildSavedItems = (ids) => {
    return ids.map((productId) => {
      const p = safeProducts.find(
        (item) => String(item._id) === String(productId),
      );
      return {
        productId,
        name: p?.name || "",
        store: p?.store || "",
        entrepreneur: p?.entrepreneur || "",
        city: p?.city || "",
        price: p?.sellingPrice || 0,
        quantity: 1,
      };
    });
  };

  const persistCart = (nextCartMap) => {
    const items = buildCartItems(nextCartMap);
    cartQueueRef.current = cartQueueRef.current
      .then(() => syncCart(items))
      .catch((e) => console.error("Cart sync error:", e));
  };

  const persistSaved = (nextIds) => {
    const items = buildSavedItems(nextIds);
    savedQueueRef.current = savedQueueRef.current
      .then(() => syncSaved(items))
      .catch((e) => console.error("Saved sync error:", e));
  };

  const loadMyKart = async () => {
    setLoadingKart(true);
    setMessage(null);
    try {
      const [cartRes, savedRes] = await Promise.all([
        fetchCart(),
        fetchSaved(),
      ]);

      if (cartRes?.success) {
        const cartMap = {};
        (cartRes.items || []).forEach((item) => {
          cartMap[item.productId] = item.quantity;
        });
        setCart(cartMap);
      }

      if (savedRes?.success) {
        setSavedIds((savedRes.items || []).map((item) => item.productId));
      }
    } catch (e) {
      console.error("Kart fetch error:", e);
      setMessage({
        type: "error",
        text: "Could not load your cart/wishlist. Please try again.",
      });
    } finally {
      setLoadingKart(false);
    }
  };

  useEffect(() => {
    loadMyKart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    const loadProduct = async () => {
      setLoadingProduct(true);
      try {
        // const response = await fetchLiveProducts();
        // if (response?.success) {
        //   setLiveProducts(response.products || []);
        // }
        const response = await fetchLiveProducts();
        if (response?.success) {
          setLiveProducts(
            (response.products || []).map((p) => ({ ...p, isSample: false })),
          );
        }
      } catch (e) {
        console.error("Product fetch error:", e);
      } finally {
        setLoadingProduct(false);
      }
    };
    loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Look in live products first, fall back to sample/fallback data if not found there.
  const allProducts = useMemo(
    () => [...liveProducts, ...safeProducts],
    [liveProducts, safeProducts],
  );

  const product = useMemo(() => {
    return allProducts.find((item) => String(item._id) === String(id));
  }, [id, allProducts]);

  const similarProducts = useMemo(() => {
    if (!product) return [];

    return allProducts
      .filter(
        (item) =>
          item._id !== product._id && item.category === product.category,
      )
      .slice(0, 4);
  }, [product, allProducts]);

  if (loadingProduct) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-100">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#050816] px-6 text-center text-white">
        <FontAwesomeIcon
          icon={faBoxOpen}
          className="mb-5 text-6xl text-slate-300"
        />

        <h2 className="text-2xl font-bold">Product not found</h2>

        <p className="mt-2 text-slate-200">This product doesn't exist.</p>

        <button
          onClick={() => navigate(-1)}
          className="mt-4 rounded-xl bg-pink-600 w-fit px-2 py-1 font-semibold hover:bg-pink-500"
        >
          Go Back
        </button>
      </div>
    );
  }

  const quantity = cart[product._id] || 0;

  const handleAdd = () => {
    if (product.isSample) return;
    setCart((prev) => {
      const next = { ...prev, [product._id]: (prev[product._id] || 0) + 1 };
      persistCart(next);
      return next;
    });
  };

  const handleIncrease = () => {
    if (product.isSample) return;
    setCart((prev) => {
      const next = { ...prev, [product._id]: (prev[product._id] || 0) + 1 };
      persistCart(next);
      return next;
    });
  };

  const handleDecrease = () => {
    if (product.isSample) return;
    setCart((prev) => {
      const qty = prev[product._id] || 0;
      let next;

      if (qty <= 1) {
        next = { ...prev };
        delete next[product._id];
      } else {
        next = { ...prev, [product._id]: qty - 1 };
      }

      persistCart(next);
      return next;
    });
  };

  const handleSave = () => {
    if (product.isSample) return;
    setSavedIds((prev) => {
      const next = prev.includes(product._id)
        ? prev.filter((item) => item !== product._id)
        : [...prev, product._id];

      persistSaved(next);
      return next;
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.name,
          url: window.location.href,
        });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(window.location.href);
      setMessage({ type: "success", text: "Product link copied." });
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!feedbackRating && !feedbackText.trim()) {
      setMessage({
        type: "error",
        text: "Please give a rating or write feedback.",
      });
      return;
    }

    setSubmittingFeedback(true);
    try {
      const response = await submitFeedback({
        publisherCode: product.sellerCode,
        level: "product",
        refCode: String(product._id),
        refName: product.name,
        rating: feedbackRating,
        message: feedbackText.trim(),
      });

      if (response?.success) {
        setMessage({ type: "success", text: "Thank you for your feedback!" });
        setFeedbackRating(0);
        setFeedbackText("");
      } else {
        setMessage({ type: "error", text: "Failed to submit feedback." });
      }
    } catch (e) {
      console.error("Feedback submit error:", e);
      setMessage({ type: "error", text: "Failed to submit feedback." });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSampleRequest = async (e) => {
    e.preventDefault();
    setMessage(null);

    setSubmittingSample(true);
    try {
      const response = await submitSampleRequest({
        publisherCode: product.sellerCode,
        type: "Sample",
        refCode: String(product._id),
        refName: product.name,
        note: sampleNote.trim(),
      });

      if (response?.success) {
        setMessage({
          type: "success",
          text: "Sample request sent! The seller will get in touch soon.",
        });
        setSampleNote("");
        setShowSampleForm(false);
      } else {
        setMessage({ type: "error", text: "Failed to send sample request." });
      }
    } catch (e) {
      console.error("Sample request error:", e);
      setMessage({ type: "error", text: "Failed to send sample request." });
    } finally {
      setSubmittingSample(false);
    }
  };

  // const handleAddSimilar = (item) => {
  //   setCart((prev) => {
  //     const next = { ...prev, [item._id]: (prev[item._id] || 0) + 1 };
  //     persistCart(next);
  //     return next;
  //   });
  // };

  // const handleIncreaseSimilar = (item) => {
  //   setCart((prev) => {
  //     const next = { ...prev, [item._id]: (prev[item._id] || 0) + 1 };
  //     persistCart(next);
  //     return next;
  //   });
  // };

  // const handleDecreaseSimilar = (item) => {
  //   setCart((prev) => {
  //     const qty = prev[item._id] || 0;
  //     let next;

  //     if (qty <= 1) {
  //       next = { ...prev };
  //       delete next[item._id];
  //     } else {
  //       next = { ...prev, [item._id]: qty - 1 };
  //     }

  //     persistCart(next);
  //     return next;
  //   });
  // };

  const handleAddSimilar = (item) => {
    if (item.isSample) return;
    setCart((prev) => {
      const next = { ...prev, [item._id]: (prev[item._id] || 0) + 1 };
      persistCart(next);
      return next;
    });
  };

  const handleIncreaseSimilar = (item) => {
    if (item.isSample) return;
    setCart((prev) => {
      const next = { ...prev, [item._id]: (prev[item._id] || 0) + 1 };
      persistCart(next);
      return next;
    });
  };

  const handleDecreaseSimilar = (item) => {
    if (item.isSample) return;
    setCart((prev) => {
      const qty = prev[item._id] || 0;
      let next;

      if (qty <= 1) {
        next = { ...prev };
        delete next[item._id];
      } else {
        next = { ...prev, [item._id]: qty - 1 };
      }

      persistCart(next);
      return next;
    });
  };

  const handleToggleSaveSimilar = (item) => {
    setSavedIds((prev) => {
      const next = prev.includes(item._id)
        ? prev.filter((sid) => sid !== item._id)
        : [...prev, item._id];

      persistSaved(next);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/products"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-sm lg:text-lg font-bold text-cyan-200">
                Product Details
              </h1>
              <p className="text-xs text-slate-200">FoundHer Marketplace</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* <button
              onClick={loadMyKart}
              className="rounded-xl bg-white/10 w-fit px-2 py-1 text-xs hover:bg-white/20"
              title="Refresh cart"
            >
              {loadingKart ? "Loading..." : "🔄 Refresh"}
            </button> */}

            <button
              onClick={handleShare}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faShareAlt} />
            </button>

            <button
              onClick={handleSave}
              disabled={product.isSample}
              title={product.isSample ? "Sample products can't be saved" : "Save"}
              className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                product.isSample
                  ? "cursor-not-allowed bg-white/5 opacity-40"
                  : "bg-white/10 hover:bg-white/20"
              }`}
            >
              {savedIds.includes(product._id) ? (
                <FontAwesomeIcon icon={faHeart} className="text-pink-500" />
              ) : (
                <FontAwesomeIcon icon={faRegHeart} />
              )}
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-7xl px-4 pt-2">
          <div
            className={`flex items-center gap-3 rounded-2xl border px-4 py-2 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon
              icon={
                message.type === "success" ? faCircleCheck : faCircleExclamation
              }
            />
            {message.text}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 py-5"></div>
      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/5">
            <img
              src={product.images?.[0] || product.image}
              alt={product.name}
              className="h-80 w-full object-cover md:h-[520px]"
            />
          </div>
        </div>

        <div>
          <span className="inline-flex rounded-full bg-pink-600/20 px-3 py-1 text-xs font-semibold text-pink-300">
            {product.category}
          </span>

          <div className="mt-2 flex items-center justify-between gap-3">
            <h2 className="text-xl lg:text-2xl font-bold text-cyan-200">
              {product.name}
            </h2>

            {product.tts && (
              <button
                type="button"
                onClick={handleToggleSpeakTTS}
                title={isSpeakingTTS ? "Stop" : "Listen to product description"}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all ${
                  isSpeakingTTS
                    ? "bg-pink-500 text-white"
                    : "bg-white/10 text-pink-300 hover:bg-white/20"
                }`}
              >
                <FontAwesomeIcon icon={isSpeakingTTS ? faVolumeXmark : faVolumeHigh} />
              </button>
            )}
          </div>

          <div className="mt-2 flex items-center gap-4">
            <span className="text-xl lg:text-2xl font-bold text-pink-400">
              ₹{product.sellingPrice}
            </span>

            {product.mrp > product.sellingPrice && (
              <span className="text-lg text-slate-300 line-through">
                ₹{product.mrp}
              </span>
            )}
          </div>

          <div className="mt-2 space-y-3">
            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
              <FontAwesomeIcon icon={faStore} className="text-pink-400" />
              <div>
                <p className="text-xs text-slate-200">Store</p>
                <p className="font-semibold text-gray-100">{product.store}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
              <FontAwesomeIcon icon={faUserTie} className="text-pink-400" />
              <div>
                <p className="text-xs text-slate-200">Entrepreneur</p>
                <p className="font-semibold text-gray-100">
                  {product.entrepreneur}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="text-pink-400"
              />
              <div>
                <p className="text-xs text-slate-200">Location</p>
                <p className="font-semibold text-gray-100">
                  {product.locality}, {product.city}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
              <FontAwesomeIcon icon={faTruck} className="text-pink-400" />
              <div>
                <p className="font-semibold text-gray-100">
                  Cash On Delivery Available
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-emerald-400"
              />
              <div>
                <p className="font-semibold text-gray-100">In Stock</p>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faTag} className="text-pink-400" />
              <h3 className="font-semibold text-cyan-200">Description</h3>
            </div>

            <p className="mt-3 text-sm leading-7 text-slate-100">
              {product.description ||
                "Premium quality product created by a women entrepreneur through the FoundHer ecosystem. Every purchase supports independent women-led businesses while promoting local entrepreneurship."}
            </p>
          </div>

          <div className="mt-4 rounded-2xl border border-purple-500/20 bg-white/5 p-5">
            <button
              type="button"
              onClick={() => setEnableAskAI((prev) => !prev)}
              className={`w-fit px-2 py-2 rounded-sm text-sm lg:text-lg font-bold transition-all ${
                enableAskAI
                  ? "bg-purple-500 text-white"
                  : "bg-[#111827] text-purple-300 border border-purple-500/30"
              }`}
            >
              {enableAskAI ? "🙈 Hide GenAI" : "🤖 Ask AI About This Product"}
            </button>

            {enableAskAI && (
              <div className="mt-3 w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                <AILightComponent
                  mode="herkart_product_customer_qna"
                  param1={"🗣️ Ask AI About This Product"}
                  param2={`
Product Details:
Name: ${product.name || "N/A"}
Category: ${product.category || "N/A"}
Description: ${product.description || "N/A"}
Entrepreneur/Brand: ${product.entrepreneur || "N/A"}
MRP: ${product.mrp || "N/A"} rupees
Selling Price: ${product.sellingPrice || "N/A"} rupees
Unit: ${product.unit || "N/A"}
Locality/City: ${product.locality || ""} ${product.city || ""}
Remarks: ${product.remarks || "N/A"}
Listing Type: ${product.isSample ? "This is a SAMPLE/DEMO listing, not available for actual purchase." : "This is a live product available for purchase."}

You are a friendly voice shopping assistant. A customer is browsing this product on HerKart, a marketplace for women-led businesses, and wants to hear about it spoken aloud.

Using only the details above, speak naturally and warmly about the product — what makes it worth buying, who it's from, and anything a shopper would want to know before purchasing.

If the Listing Type above says this is a sample/demo listing, mention early and clearly in your answer that this specific item is a demo listing and isn't actually available to buy right now, without making that the whole focus — still describe the product itself normally after that.

Do NOT invent facts not present above. If details are missing, skip them gracefully instead of guessing.

STRICT OUTPUT RULES:
1. Output ONLY the spoken answer itself — nothing else.
2. Do NOT include any headings, labels, meta-commentary, introductions like "Here's what I found" or "Sure, here's the answer", disclaimers, or explanations about what you are doing.
3. Do NOT use markdown, bullet points, numbering, emojis, or asterisks — this will be read aloud or read as plain text exactly as written.
4. Write it exactly as you would say it out loud to the customer standing in front of you — natural spoken sentences only.
5. Keep it conversational and warm, like a helpful shop assistant, not a sales pitch.
`}
                />
              </div>
            )}
          </div>

          <div className="mt-4 space-y-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 rounded-xl bg-white/10 w-fit px-2 py-1 text-sm hover:bg-white/20"
            >
              ← Back to Products
            </button>

            {product.isSample ? (
              <div className="flex w-fit items-center gap-2 rounded-2xl bg-white/10 px-3 py-2 text-sm text-slate-400">
                <FontAwesomeIcon icon={faShoppingCart} />
                Sample — Not for Sale
              </div>
            ) : quantity === 0 ? (
              <button
                onClick={handleAdd}
                className="flex w-fit px-2 py-1 items-center justify-center gap-2 rounded-2xl bg-pink-600 font-semibold transition hover:bg-pink-500"
              >
                <FontAwesomeIcon icon={faShoppingCart} />
                Add to Cart
              </button>
            ) : (
              <>
                <div className="rounded-2xl border border-pink-500 bg-pink-600/10 p-4 text-center">
                  <p className="text-pink-300 font-medium">
                    ✓ Already in Cart ({quantity})
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDecrease}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-600 hover:bg-pink-500"
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>

                  <span className="text-xl font-bold min-w-[40px] text-center">
                    {quantity}
                  </span>

                  <button
                    onClick={handleIncrease}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-600 hover:bg-pink-500"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Request Sample - Only if allowed */}
          {product.isSample && (
            <div className="mt-2">
              {!showSampleForm ? (
                <button
                  onClick={() => setShowSampleForm(true)}
                  className="flex w-fit px-2 py-1 items-center gap-2 rounded-xl border border-white/10 bg-white/5 text-xs hover:bg-white/10"
                >
                  <FontAwesomeIcon icon={faFlask} />
                  Request Sample
                </button>
              ) : (
                <form
                  onSubmit={handleSampleRequest}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2"
                >
                  <label className="mb-2 block text-sm text-slate-100">
                    Note (optional)
                  </label>
                  <textarea
                    value={sampleNote}
                    onChange={(e) =>
                      setSampleNote(e.target.value.slice(0, 250))
                    }
                    style={{ height: "100px" }}
                    maxLength={250}
                    placeholder="Any specific request..."
                    className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm outline-none focus:border-pink-500"
                  />
                  <div className="mt-1 text-right text-xs text-slate-300">
                    {sampleNote.length}/250
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button
                      type="submit"
                      disabled={submittingSample}
                      className="rounded-xl bg-pink-600 w-fit px-2 py-1 text-xs font-semibold hover:bg-pink-500 disabled:opacity-60"
                    >
                      {submittingSample ? "Sending..." : "Send Request"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSampleForm(false)}
                      className="rounded-xl border border-white/10 w-fit px-2 py-1 text-xs hover:bg-white/10"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Feedback */}
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
            <h3 className="mb-3 font-semibold text-cyan-200">Leave Feedback</h3>

            <form onSubmit={handleFeedbackSubmit}>
              <div className="mb-3 flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFeedbackRating(star)}
                    onMouseEnter={() => setFeedbackHoverRating(star)}
                    onMouseLeave={() => setFeedbackHoverRating(0)}
                    className="text-2xl"
                  >
                    <FontAwesomeIcon
                      icon={faStar}
                      className={
                        (feedbackHoverRating || feedbackRating) >= star
                          ? "text-amber-400"
                          : "text-slate-400"
                      }
                    />
                  </button>
                ))}
              </div>

              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value.slice(0, 250))}
                style={{ height: "100px" }}
                maxLength={250}
                placeholder="Share your experience with this product (optional if you've given a rating)..."
                className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm outline-none focus:border-pink-500"
              />
              <div className="mt-1 text-right text-xs text-slate-300">
                {feedbackText.length}/250
              </div>

              <button
                type="submit"
                disabled={submittingFeedback}
                className="mt-3 rounded-xl bg-pink-600 w-fit px-2 py-1 text-sm font-semibold hover:bg-pink-500 disabled:opacity-60"
              >
                {submittingFeedback ? "Submitting..." : "Submit Feedback"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {similarProducts.length > 0 && (
        <section className="mt-14">
          <h3 className="mb-6 text-2xl font-bold text-yellow-200">
            Similar Products
          </h3>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {similarProducts.map((item) => (
              <ProductCard
                key={item._id}
                product={item}
                quantity={cart[item._id] || 0}
                isSaved={savedIds.includes(item._id)}
                onAdd={() => handleAddSimilar(item)}
                onIncrease={() => handleIncreaseSimilar(item)}
                onDecrease={() => handleDecreaseSimilar(item)}
                onToggleSave={() => handleToggleSaveSimilar(item)}
                onView={() => navigate(`/herkart/product/${item._id}`)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default HerKartProductDetails;
